import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Plus, Check, Star, ThumbsUp, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import MovieRow from "../components/MovieRow";
import Footer from "../components/Footer";

export default function Detail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [inList, setInList] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [episodes, setEpisodes] = useState([]);
  const [activeSeason, setActiveSeason] = useState(1);
  const [partyStats, setPartyStats] = useState({ viewers: 0, parties: 0 });

  // Memoize expensive per-render derivations so the Episodes section doesn't
  // recompute the unique-season set + sorted episode list on every render.
  const seasonNumbers = useMemo(
    () => [...new Set(episodes.map((e) => e.season))].sort((a, b) => a - b),
    [episodes],
  );
  const activeSeasonEpisodes = useMemo(
    () =>
      episodes
        .filter((e) => e.season === activeSeason)
        .sort((a, b) => a.episode_number - b.episode_number),
    [episodes, activeSeason],
  );

  const load = async () => {
    const { data } = await api.get(`/videos/${id}`);
    setV(data);
    const [wl, rv, all] = await Promise.all([
      api.get("/watchlist").catch(() => ({ data: [] })),
      api.get(`/reviews/${id}`),
      api.get("/videos", { params: { limit: 50 } }),
    ]);
    setInList(wl.data.some((x) => x.id === id));
    setReviews(rv.data);
    const sim = all.data.filter((x) => x.id !== id && x.genres?.some((g) => data.genres?.includes(g))).slice(0, 12);
    setSimilar(sim);
    if (data.type === "series") {
      try {
        const eps = await api.get(`/videos/${id}/episodes`);
        setEpisodes(eps.data);
        if (eps.data.length) setActiveSeason(eps.data[0].season);
      } catch (e) {
        console.warn("[detail] episodes fetch failed", e?.response?.status);
      }
    }
  };

  useEffect(() => { load(); }, [id]);

  // Live "Watching now" social-proof poll
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      api.get(`/watchparty/stats/${id}`)
        .then((r) => { if (!cancelled) setPartyStats(r.data); })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  const toggleList = async () => {
    try {
      if (inList) { await api.delete(`/watchlist/${id}`); setInList(false); toast.success("Removed from My List"); }
      else { await api.post(`/watchlist/${id}`); setInList(true); toast.success("Added to My List"); }
    } catch { toast.error("Failed"); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/reviews/${id}`, { rating, comment });
      const { data } = await api.get(`/reviews/${id}`);
      setReviews(data); setComment(""); toast.success("Review posted");
    } catch { toast.error("Failed"); }
  };

  if (!v) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-[#E50914] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar transparent />
      <section className="relative h-[75vh] min-h-[500px]">
        {v.trailer_yt ? (
          <iframe
            className="absolute inset-0 w-full h-full pointer-events-none"
            src={`https://www.youtube.com/embed/${v.trailer_yt}?autoplay=1&mute=1&loop=1&controls=0&playlist=${v.trailer_yt}&modestbranding=1`}
            allow="autoplay; encrypted-media"
            title="trailer"
          />
        ) : (
          <img src={v.backdrop} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-16 px-6 lg:px-12 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-black">{v.title}</h1>
          {partyStats.viewers > 0 && (
            <div data-testid="watching-now-badge" className="inline-flex items-center gap-2 mt-3 bg-cyan-500/15 border border-cyan-400/30 backdrop-blur px-3 py-1 rounded-full text-xs w-fit animate-fade-up">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-300"></span>
              </span>
              <span className="text-cyan-100 font-semibold">{partyStats.viewers} watching now</span>
              <span className="text-cyan-300/70">· {partyStats.parties} active {partyStats.parties === 1 ? "party" : "parties"}</span>
            </div>
          )}
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-300">
            <span className="text-green-400 font-semibold">{Math.round(v.tmdb_rating * 10)}% Match</span>
            <span>{v.year}</span>
            <span className="border border-white/30 px-2 text-xs">{v.rating}</span>
            <span>{v.duration_min} min</span>
            <span className="capitalize">{v.type}</span>
          </div>
          <p className="mt-4 text-lg text-gray-200 max-w-2xl">{v.description}</p>
          <div className="flex items-center gap-3 mt-6">
            <button data-testid="detail-play-btn" onClick={() => nav(`/watch/${id}`)} className="btn-red px-8 py-3 rounded font-semibold flex items-center gap-2 text-lg">
              <Play className="w-5 h-5 fill-current" /> Play
            </button>
            <button data-testid="detail-addlist-btn" onClick={toggleList} className="btn-glass p-3 rounded-full">
              {inList ? <Check className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
            </button>
            <button className="btn-glass p-3 rounded-full"><ThumbsUp className="w-6 h-6" /></button>
            <button
              data-testid="detail-share-btn"
              onClick={async () => {
                // Auto-tag with utm_content=<video-id> so the breakdown table shows which title drives the signups
                const params = new URLSearchParams();
                if (user?.referral_code) params.set("ref", user.referral_code);
                params.set("utm_source", "direct");
                params.set("utm_medium", "trailer-share");
                params.set("utm_campaign", "detail-page");
                params.set("utm_content", id);
                const url = `${window.location.origin}/share/${id}?${params.toString()}`;
                try {
                  if (navigator.share) {
                    await navigator.share({ title: v.title, text: `Watch the trailer for ${v.title}`, url });
                  } else {
                    await navigator.clipboard.writeText(url);
                    toast.success("Share link copied!");
                  }
                } catch {}
              }}
              className="btn-glass p-3 rounded-full"
              aria-label="share trailer"
            >
              <Share2 className="w-6 h-6" />
            </button>
            <button
              data-testid="detail-watchparty-btn"
              onClick={async () => {
                if (user?.subscription_status !== "active") {
                  toast.error("Active subscription required to host a Watch Party");
                  return;
                }
                try {
                  const { data } = await api.post("/watchparty", { video_id: id });
                  const link = `${window.location.origin}/watch/${id}?party=${data.room_id}`;
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: `Watch ${v.title} together`, url: link });
                    } else {
                      await navigator.clipboard.writeText(link);
                    }
                    toast.success("Watch Party link copied!");
                  } catch {}
                  nav(`/watch/${id}?party=${data.room_id}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not start Watch Party");
                }
              }}
              className="btn-glass p-3 rounded-full"
              aria-label="start watch party"
              title="Start Watch Party"
            >
              <Users className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-10 max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-3">
          <h3 className="text-xl font-semibold">About {v.title}</h3>
          <p className="text-gray-300">{v.description}</p>
          <p className="text-sm text-gray-400"><span className="text-gray-500">Genres:</span> {v.genres?.join(", ")}</p>
          {v.cast?.length > 0 && <p className="text-sm text-gray-400"><span className="text-gray-500">Cast:</span> {v.cast.join(", ")}</p>}
        </div>
        <div className="bg-[#141414] border border-white/5 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2"><Star className="w-5 h-5 text-yellow-500 fill-current" /><span className="text-2xl font-bold">{v.tmdb_rating}</span></div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">TMDB Rating</p>
          <div className="mt-4 flex items-center gap-2"><Star className="w-5 h-5 text-[#E50914] fill-current" /><span className="text-2xl font-bold">{reviews.average || "—"}</span></div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">User Reviews ({reviews.count})</p>
        </div>
      </section>

      {/* Episodes (series only) */}
      {v.type === "series" && episodes.length > 0 && (
        <section data-testid="episodes-section" className="px-6 lg:px-12 py-8 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-semibold">Episodes</h3>
            <select
              data-testid="season-select"
              value={activeSeason}
              onChange={(e) => setActiveSeason(parseInt(e.target.value))}
              className="input-dark text-sm py-2 w-auto"
            >
              {seasonNumbers.map(s => (
                <option key={s} value={s}>Season {s}</option>
              ))}
            </select>
          </div>
          <div className="space-y-3">
            {activeSeasonEpisodes.map(ep => (
              <button
                key={ep.episode_id}
                data-testid={`episode-${ep.episode_id}`}
                onClick={() => nav(`/watch/${id}?ep=${ep.episode_id}`)}
                className="w-full text-left bg-[#141414] border border-white/5 hover:border-[#E50914]/40 rounded-lg p-4 flex gap-4 group transition-all"
              >
                <div className="text-3xl font-black text-gray-600 group-hover:text-[#E50914] w-10 flex-shrink-0">{ep.episode_number}</div>
                {ep.thumbnail ? (
                  <img src={ep.thumbnail} alt="" className="w-32 h-20 object-cover rounded flex-shrink-0" />
                ) : (
                  <div className="w-32 h-20 bg-[#1a1a1a] rounded flex items-center justify-center flex-shrink-0">
                    <Play className="w-8 h-8 text-gray-600 group-hover:text-[#E50914]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold">{ep.title}</h4>
                    <span className="text-xs text-gray-500">{ep.duration_min}m</span>
                  </div>
                  <p className="text-sm text-gray-400 line-clamp-2">{ep.description || "No description"}</p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="px-6 lg:px-12 py-10 max-w-4xl mx-auto">
        <h3 className="text-2xl font-semibold mb-6">Reviews</h3>
        <form onSubmit={submitReview} className="bg-[#141414] border border-white/5 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            {[1,2,3,4,5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)}>
                <Star className={`w-6 h-6 ${n <= rating ? "text-[#E50914] fill-current" : "text-gray-600"}`} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Share your thoughts..." className="input-dark w-full" />
          <button data-testid="review-submit" className="btn-red mt-3 px-5 py-2 rounded font-semibold">Post Review</button>
        </form>
        <div className="space-y-4">
          {reviews.reviews.map((r) => (
            <div key={r.review_id} className="bg-[#141414] border border-white/5 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">{r.user_name}</span>
                <div className="flex items-center gap-1">
                  {[...Array(r.rating)].map((_,i) => <Star key={i} className="w-4 h-4 text-[#E50914] fill-current" />)}
                </div>
              </div>
              <p className="text-gray-300 text-sm">{r.comment}</p>
            </div>
          ))}
          {reviews.reviews.length === 0 && <p className="text-gray-500 text-center py-6">Be the first to review!</p>}
        </div>
      </section>

      <MovieRow title="More Like This" videos={similar} />
      <Footer />
    </div>
  );
}
